import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { 
  Clock, 
  MapPin, 
  CheckCircle, 
  XCircle, 
  LogIn, 
  LogOut,
  AlertTriangle,
  Navigation,
  Loader2,
  Wifi,
  WifiOff,
  Globe,
  Building2,
  Calendar,
  TrendingUp,
  Download,
  FileText,
  Users,
  Shield,
  Network,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const API_URL = '';

export default function MyAttendancePage() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("today");
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [lateReason, setLateReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const isLateNow = () => new Date(Date.now() + 11 * 60 * 60 * 1000).getUTCHours() >= 8;
  const finalLateReason = lateReason === "Other" ? otherReason.trim() : lateReason;
  const [signingOut, setSigningOut] = useState(false);
  const [resettingAttendance, setResettingAttendance] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Status and records
  const [todayStatus, setTodayStatus] = useState(null);
  const [networkStatus, setNetworkStatus] = useState(null);
  const [monthlyRecords, setMonthlyRecords] = useState(null);
  const [summary, setSummary] = useState(null);
  
  // Filters
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Admin: School networks
  const [schoolNetworks, setSchoolNetworks] = useState([]);
  const [registeringNetwork, setRegisteringNetwork] = useState(false);
  
  const isAdmin = ['principal', 'deputy_principal', 'academic_principal', 'admin', 'coordinator'].includes(user?.role);

  useEffect(() => {
    // Check both 'user' and 'currentUser' for compatibility
    const userData = localStorage.getItem("user") || localStorage.getItem("currentUser");
    if (userData) {
      setUser(JSON.parse(userData));
    }
    
    // Update time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (user) {
      fetchTodayStatus();
      checkNetworkStatus();
    }
  }, [user]);

  useEffect(() => {
    if (user && activeTab === "history") {
      fetchMonthlyRecords();
    }
  }, [user, activeTab, selectedMonth, selectedYear]);

  useEffect(() => {
    if (user && activeTab === "networks" && isAdmin) {
      fetchSchoolNetworks();
    }
  }, [user, activeTab, isAdmin]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  };

  // Safe JSON parse helper
  const safeJson = async (response) => {
    if (response.bodyUsed) return null;
    try { return await response.json(); } catch { return null; }
  };

  const fetchTodayStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teacher-attendance/my-status`, {
        headers: getAuthHeaders()
      });
      const data = await safeJson(response);
      if (data) {
        setTodayStatus(data);
        setSummary(data);
      }
    } catch (error) {
      console.error("Error fetching status:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkNetworkStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teacher-attendance/check-network`, {
        headers: getAuthHeaders()
      });
      const data = await safeJson(response);
      if (data) setNetworkStatus(data);
    } catch (error) {
      console.error("Error checking network:", error);
    }
  };

  const fetchMonthlyRecords = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/teacher-attendance/my-records?month=${selectedMonth}&year=${selectedYear}`,
        { headers: getAuthHeaders() }
      );
      const data = await safeJson(response);
      if (data) setMonthlyRecords(data);
    } catch (error) {
      console.error("Error fetching records:", error);
    }
  };

  const fetchSchoolNetworks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teacher-attendance/school-networks`, {
        headers: getAuthHeaders()
      });
      const data = await safeJson(response);
      if (data) setSchoolNetworks(data.networks || []);
    } catch (error) {
      console.error("Error fetching networks:", error);
    }
  };

  const getLocation = () => {
    return new Promise((resolve, reject) => {
      setGettingLocation(true);
      setLocationError(null);
      
      if (!navigator.geolocation) {
        setLocationError("Geolocation is not supported by your browser");
        setGettingLocation(false);
        reject(new Error("Geolocation not supported"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setLocation(loc);
          setGettingLocation(false);
          resolve(loc);
        },
        (error) => {
          let errorMsg = "Unable to get your location";
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMsg = "Location permission denied. Please enable location access.";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMsg = "Location information unavailable";
              break;
            case error.TIMEOUT:
              errorMsg = "Location request timed out";
              break;
          }
          setLocationError(errorMsg);
          setGettingLocation(false);
          reject(new Error(errorMsg));
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  const handleSignIn = async () => {
    if (isLateNow() && !finalLateReason) {
      toast.error(lateReason === "Other" ? "Please explain your reason for arriving late" : "Please select a reason for arriving late");
      return;
    }
    try {
      setSigningIn(true);
      let loc = { latitude: 0, longitude: 0 };
      
      try {
        loc = await getLocation();
      } catch (locError) {
        // Allow sign-in without GPS - use network detection instead
        console.log("Location unavailable, proceeding with network-based sign-in");
        setLocationError(null); // Clear error since we'll use network detection
      }
      
      const response = await fetch(`${API_URL}/api/teacher-attendance/sign-in`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ latitude: loc.latitude, longitude: loc.longitude, late_reason: finalLateReason || null })
      });

      const data = await safeJson(response);
      
      if (response.ok && data) {
        toast.success(
          <div>
            <p className="font-medium">Signed in successfully!</p>
            <p className="text-sm text-muted-foreground">
              {data.is_on_school_network ? "On school network" : "Remote sign-in"}
            </p>
          </div>
        );
        fetchTodayStatus();
        checkNetworkStatus();
      } else {
        toast.error(data?.detail || "Failed to sign in");
      }
    } catch (error) {
      toast.error("Failed to sign in. Please try again.");
    } finally {
      setSigningIn(false);
    }
  };

  const handleSignOut = async () => {
    try {
      setSigningOut(true);
      let loc = location;
      
      try {
        loc = await getLocation();
      } catch (e) {
        // Continue without location for sign out
      }
      
      const response = await fetch(`${API_URL}/api/teacher-attendance/sign-out`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          latitude: loc?.latitude || 0, 
          longitude: loc?.longitude || 0 
        })
      });

      const data = await safeJson(response);
      
      if (response.ok && data) {
        toast.success("Signed out successfully!");
        fetchTodayStatus();
      } else {
        toast.error(data?.detail || "Failed to sign out");
      }
    } catch (error) {
      toast.error("Failed to sign out. Please try again.");
    } finally {
      setSigningOut(false);
    }
  };

  const handleResetAttendance = async () => {
    if (!confirm("Are you sure you want to reset today's attendance? This will allow you to sign in again.")) {
      return;
    }
    
    try {
      setResettingAttendance(true);
      const response = await fetch(`${API_URL}/api/teacher-attendance/reset-today`, {
        method: "POST",
        headers: getAuthHeaders()
      });

      const data = await safeJson(response);
      
      if (response.ok && data) {
        toast.success("Attendance reset! You can sign in again.");
        setLocationError(null);
        fetchTodayStatus();
      } else {
        toast.error(data?.detail || "Failed to reset attendance");
      }
    } catch (error) {
      toast.error("Failed to reset attendance");
    } finally {
      setResettingAttendance(false);
    }
  };

  const handleRegisterNetwork = async () => {
    try {
      setRegisteringNetwork(true);
      const response = await fetch(`${API_URL}/api/teacher-attendance/register-network`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ network_name: `${user?.school || 'School'} Network` })
      });

      const data = await safeJson(response);
      
      if (response.ok && data) {
        if (data.already_registered) {
          toast.info("This network is already registered");
        } else {
          toast.success(`Network registered: ${data.ip_address}`);
        }
        fetchSchoolNetworks();
        checkNetworkStatus();
      } else {
        toast.error(data?.detail || "Failed to register network");
      }
    } catch (error) {
      toast.error("Failed to register network");
    } finally {
      setRegisteringNetwork(false);
    }
  };

  // Vanuatu/Solomon Islands timezone: UTC+11 hours
  const VANUATU_OFFSET_MS = 11 * 60 * 60 * 1000;
  
  const formatTime = (isoString) => {
    if (!isoString) return "-";
    
    // Ensure we parse as UTC by adding 'Z' if not present
    let utcString = isoString;
    if (!isoString.endsWith('Z') && !isoString.includes('+')) {
      utcString = isoString + 'Z';
    }
    
    // Parse the UTC time from database
    const utcDate = new Date(utcString);
    
    // Convert to Vanuatu time (UTC+11) by adding offset
    const vanuatuMs = utcDate.getTime() + VANUATU_OFFSET_MS;
    const vanuatuDate = new Date(vanuatuMs);
    
    // Extract hours and minutes from the adjusted time
    const hours = vanuatuDate.getUTCHours();
    const minutes = vanuatuDate.getUTCMinutes();
    
    // Determine AM/PM based on 24-hour value
    const ampm = hours >= 12 ? 'PM' : 'AM';
    
    // Convert to 12-hour format (12 stays as 12, 0 becomes 12)
    const hour12 = hours % 12 || 12;
    
    // Format with leading zeros
    return `${hour12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getNetworkBadge = (isOnNetwork, status) => {
    if (isOnNetwork) {
      return (
        <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
          <Wifi className="h-3 w-3" />
          On-Site
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 flex items-center gap-1">
        <Globe className="h-3 w-3" />
        Remote
      </Badge>
    );
  };

  const exportToCSV = () => {
    if (!monthlyRecords?.records) return;
    
    const headers = ["Date", "Sign In", "Sign Out", "Hours", "Location", "Network Status", "Late"];
    const rows = monthlyRecords.records.map(r => [
      r.date,
      formatTime(r.sign_in_time),
      formatTime(r.sign_out_time),
      r.hours_worked || "-",
      r.location_status || "-",
      r.is_on_school_network ? "On-Site" : "Remote",
      r.is_late ? "Yes" : "No"
    ]);
    
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_${selectedMonth}_${selectedYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Attendance exported!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-attendance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between gap-4 md:items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Clock className="h-8 w-8 text-blue-600" />
            My Attendance
          </h1>
          <p className="text-muted-foreground mt-1">
            Track your attendance with network verification
          </p>
        </div>
        
        {/* Network Status Indicator */}
        <Card className="w-fit">
          <CardContent className="p-3 flex items-center gap-3">
            {networkStatus?.is_on_school_network ? (
              <>
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Wifi className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-green-700">On School Network</p>
                  <p className="text-xs text-muted-foreground">{networkStatus?.network_name || networkStatus?.ip_address}</p>
                </div>
              </>
            ) : (
              <>
                <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <Globe className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-orange-700">Public Network</p>
                  <p className="text-xs text-muted-foreground">IP: {networkStatus?.ip_address || "Checking..."}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 lg:grid-cols-4">
          <TabsTrigger value="today" className="flex items-center gap-2" data-testid="tab-today">
            <LogIn className="h-4 w-4" />
            Today
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2" data-testid="tab-history">
            <Calendar className="h-4 w-4" />
            History
          </TabsTrigger>
          <TabsTrigger value="summary" className="flex items-center gap-2" data-testid="tab-summary">
            <TrendingUp className="h-4 w-4" />
            Summary
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="networks" className="flex items-center gap-2" data-testid="tab-networks">
              <Network className="h-4 w-4" />
              Networks
            </TabsTrigger>
          )}
        </TabsList>

        {/* Today Tab */}
        <TabsContent value="today" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sign In/Out Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Daily Sign In/Out
                </CardTitle>
                <CardDescription>
                  {currentTime.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current Time Display */}
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-4xl font-mono font-bold">
                    {currentTime.toLocaleTimeString()}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">Vanuatu Time (VUT)</p>
                </div>

                {/* Status */}
                {todayStatus && (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span className="text-sm font-medium">Sign In Time</span>
                      <span className="font-mono">
                        {todayStatus.sign_in_time ? formatTime(todayStatus.sign_in_time) : "Not signed in"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span className="text-sm font-medium">Sign Out Time</span>
                      <span className="font-mono">
                        {todayStatus.sign_out_time ? formatTime(todayStatus.sign_out_time) : "-"}
                      </span>
                    </div>
                    {todayStatus.sign_in_time && (
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium">Network Status</span>
                        {getNetworkBadge(todayStatus.is_on_school_network, todayStatus.network_location_status)}
                      </div>
                    )}
                  </div>
                )}

                {/* Location Error - hide for admins after successful sign-in */}
                {locationError && !todayStatus?.sign_in_time && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-700">{locationError}</span>
                  </div>
                )}

                {/* Late reason - required when signing in after 8:00 AM Vanuatu time */}
                {!todayStatus?.sign_in_time && isLateNow() && (
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg space-y-3" data-testid="late-reason-section">
                    <div className="flex items-center gap-2 text-orange-700">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="text-sm font-semibold">You are signing in after 8:00 AM</span>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-orange-800">Reason for late arrival <span className="text-red-600">*</span></label>
                      <Select value={lateReason} onValueChange={setLateReason}>
                        <SelectTrigger className="w-full bg-white" data-testid="late-reason-select">
                          <SelectValue placeholder="Select a reason..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Traffic / transport delay">Traffic / transport delay</SelectItem>
                          <SelectItem value="Sick - feeling unwell">Sick - feeling unwell</SelectItem>
                          <SelectItem value="Family emergency">Family emergency</SelectItem>
                          <SelectItem value="Traditional / cultural obligation">Traditional / cultural obligation</SelectItem>
                          <SelectItem value="Church commitment">Church commitment</SelectItem>
                          <SelectItem value="Bad weather / road conditions">Bad weather / road conditions</SelectItem>
                          <SelectItem value="Medical appointment">Medical appointment</SelectItem>
                          <SelectItem value="School business / official duty">School business / official duty</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      {lateReason === "Other" && (
                        <Textarea
                          placeholder="Please explain briefly..."
                          className="bg-white text-sm"
                          value={otherReason}
                          onChange={(e) => setOtherReason(e.target.value)}
                          maxLength={200}
                          data-testid="late-reason-other"
                        />
                      )}
                      <p className="text-xs text-orange-700">Your principal will see this reason next to your sign-in time.</p>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  {!todayStatus?.sign_in_time ? (
                    <Button 
                      className="flex-1" 
                      onClick={handleSignIn}
                      disabled={signingIn || gettingLocation}
                      data-testid="btn-sign-in"
                    >
                      {signingIn || gettingLocation ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {gettingLocation ? "Getting Location..." : "Signing In..."}
                        </>
                      ) : (
                        <>
                          <LogIn className="h-4 w-4 mr-2" />
                          Sign In
                        </>
                      )}
                    </Button>
                  ) : !todayStatus?.sign_out_time ? (
                    <Button 
                      className="flex-1" 
                      variant="outline"
                      onClick={handleSignOut}
                      disabled={signingOut}
                      data-testid="btn-sign-out"
                    >
                      {signingOut ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Signing Out...
                        </>
                      ) : (
                        <>
                          <LogOut className="h-4 w-4 mr-2" />
                          Sign Out
                        </>
                      )}
                    </Button>
                  ) : (
                    <div className="flex-1 space-y-3">
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-1" />
                        <p className="text-sm font-medium text-green-700">Attendance Complete</p>
                      </div>
                      {/* Reset button for admins in case of accidental sign-out */}
                      {isAdmin && (
                        <Button 
                          variant="outline" 
                          className="w-full text-orange-600 border-orange-300 hover:bg-orange-50"
                          onClick={handleResetAttendance}
                          disabled={resettingAttendance}
                          data-testid="btn-reset-attendance"
                        >
                          {resettingAttendance ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Resetting...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Reset Today's Attendance
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Today's Stats Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Today's Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {todayStatus?.sign_in_time ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-700">
                          {todayStatus.location_status === 'at_school' ? '✓' : 
                           todayStatus.location_status === 'nearby' ? '~' : '✗'}
                        </p>
                        <p className="text-xs text-blue-600 capitalize">{todayStatus.location_status?.replace('_', ' ') || 'N/A'}</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-purple-700">
                          {todayStatus.distance_meters ? `${todayStatus.distance_meters}m` : '-'}
                        </p>
                        <p className="text-xs text-purple-600">From School</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Verification</span>
                        <Badge variant={todayStatus.verification_status === 'verified' ? 'default' : 'secondary'}>
                          {todayStatus.verification_status || 'Pending'}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Late Status</span>
                        <Badge variant={todayStatus.is_late ? 'destructive' : 'default'}>
                          {todayStatus.is_late ? 'Late' : 'On Time'}
                        </Badge>
                      </div>
                      {todayStatus.is_late && todayStatus.notes?.startsWith('Late reason:') && (
                        <div className="flex justify-between items-start gap-4">
                          <span className="text-sm shrink-0">Late reason</span>
                          <span className="text-sm text-orange-700 italic text-right" data-testid="my-late-reason">{todayStatus.notes.replace('Late reason: ', '')}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Network</span>
                        {getNetworkBadge(todayStatus.is_on_school_network)}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Sign in to see today's details</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row justify-between gap-4">
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Attendance History
                </CardTitle>
                <div className="flex gap-2">
                  <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 12}, (_, i) => (
                        <SelectItem key={i+1} value={String(i+1)}>
                          {new Date(2000, i, 1).toLocaleString('en-US', { month: 'long' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026].map(year => (
                        <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="icon" onClick={exportToCSV} title="Export to CSV">
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {monthlyRecords?.records?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Sign In</TableHead>
                      <TableHead>Sign Out</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Network</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthlyRecords.records.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell className="font-medium">{formatDate(record.date)}</TableCell>
                        <TableCell>{formatTime(record.sign_in_time)}</TableCell>
                        <TableCell>{formatTime(record.sign_out_time)}</TableCell>
                        <TableCell>{record.hours_worked ? `${record.hours_worked}h` : '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize text-xs">
                            {record.location_status?.replace('_', ' ') || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell>{getNetworkBadge(record.is_on_school_network)}</TableCell>
                        <TableCell>
                          {record.is_late && <Badge variant="destructive" className="text-xs">Late</Badge>}
                          {!record.sign_in_time && <Badge variant="secondary" className="text-xs">Absent</Badge>}
                          {record.sign_in_time && !record.is_late && <Badge className="text-xs bg-green-100 text-green-800">On Time</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No attendance records for this month</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Summary Tab */}
        <TabsContent value="summary" className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-600" />
                <p className="text-3xl font-bold">{monthlyRecords?.summary?.days_present || 0}</p>
                <p className="text-sm text-muted-foreground">Days Present</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <XCircle className="h-8 w-8 mx-auto mb-2 text-red-600" />
                <p className="text-3xl font-bold">{monthlyRecords?.summary?.days_absent || 0}</p>
                <p className="text-sm text-muted-foreground">Days Absent</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-orange-600" />
                <p className="text-3xl font-bold">{monthlyRecords?.summary?.days_late || 0}</p>
                <p className="text-sm text-muted-foreground">Days Late</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Clock className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                <p className="text-3xl font-bold">{monthlyRecords?.summary?.avg_hours_per_day || 0}h</p>
                <p className="text-sm text-muted-foreground">Avg Hours/Day</p>
              </CardContent>
            </Card>
          </div>

          {/* Network Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Network className="h-5 w-5" />
                Network Location Summary
              </CardTitle>
              <CardDescription>Where you signed in from this month</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-green-600" />
                      <span>On-Site (School Network)</span>
                    </div>
                    <span className="font-bold text-green-600">{monthlyRecords?.summary?.on_site_days || 0} days</span>
                  </div>
                  <Progress 
                    value={monthlyRecords?.summary?.days_present > 0 
                      ? (monthlyRecords.summary.on_site_days / monthlyRecords.summary.days_present) * 100 
                      : 0} 
                    className="h-2 bg-green-100"
                  />
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Globe className="h-5 w-5 text-orange-600" />
                      <span>Remote (Public Network)</span>
                    </div>
                    <span className="font-bold text-orange-600">{monthlyRecords?.summary?.remote_days || 0} days</span>
                  </div>
                  <Progress 
                    value={monthlyRecords?.summary?.days_present > 0 
                      ? (monthlyRecords.summary.remote_days / monthlyRecords.summary.days_present) * 100 
                      : 0}
                    className="h-2 bg-orange-100"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Networks Tab (Admin Only) */}
        {isAdmin && (
          <TabsContent value="networks" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      Registered School Networks
                    </CardTitle>
                    <CardDescription>
                      Manage IP addresses that identify your school's network
                    </CardDescription>
                  </div>
                  <Button onClick={handleRegisterNetwork} disabled={registeringNetwork} data-testid="btn-register-network">
                    {registeringNetwork ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Wifi className="h-4 w-4 mr-2" />
                    )}
                    Register Current Network
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Current Network Info */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Your Current Network
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">IP Address:</span>
                      <span className="ml-2 font-mono">{networkStatus?.ip_address || 'Loading...'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Status:</span>
                      <span className="ml-2">
                        {networkStatus?.is_on_school_network ? (
                          <Badge className="bg-green-100 text-green-800">Registered</Badge>
                        ) : (
                          <Badge variant="outline">Not Registered</Badge>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Registered Networks List */}
                {schoolNetworks.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Network Name</TableHead>
                        <TableHead>IP Address</TableHead>
                        <TableHead>Registered By</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {schoolNetworks.map((network) => (
                        <TableRow key={network.id}>
                          <TableCell className="font-medium">{network.network_name}</TableCell>
                          <TableCell className="font-mono">{network.ip_address}</TableCell>
                          <TableCell>{network.registered_by || '-'}</TableCell>
                          <TableCell>{formatDate(network.registered_at)}</TableCell>
                          <TableCell>
                            <Badge className={network.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}>
                              {network.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Network className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No networks registered yet</p>
                    <p className="text-sm">Click "Register Current Network" while connected to your school's WiFi</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
